import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { CodeModel } from 'src/app/models/code.model';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { RBACService } from '../../../shared/rbac.service';

@Component({
  selector: 'app-codes-edit',
  templateUrl: './sites-codes-edit.component.html',
  styleUrls: ['./sites-codes-edit.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SitesCodesEditComponent implements OnInit {
  parent_id: number;
  child_id: number;
  site_id: number;

  code: CodeModel = new CodeModel();

  features: string[] = ['Access Control'];

  types: any = {
    'Access Control': ['In', 'Out', 'In/Out', 'Location'] // , 'Evacuation'
  };

  inProgress: boolean = false;

  // RBAC permission flags. Resolved once in ngOnInit() to avoid re-evaluating on every change detection cycle.
  canDelete: boolean = false;

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    public rbac: RBACService
  ) {}

  ngOnInit() {
    // Resolve RBAC permission flags before any early returns so they are always set.
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_SITES_QR_CODES,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );

    // Get the site id from the route params.
    this.parent_id = Number(this.route.parent.snapshot.params['parent_id']);
    this.child_id = Number(this.route.parent.snapshot.params['child_id']);

    // Check if we are updating a site or creating a new one.
    if (
      this.child_id ||
      (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' &&
        this.parent_id)
    ) {
      // Store the site id.
      this.site_id = this.child_id ? this.child_id : this.parent_id;

      this.code.id = Number(this.route.snapshot.params['code_id']);

      if (this.code.id) {
        // Editing existing QR code - check view + edit permissions
        if (
          !this.rbac.userCanAccess(
            [
              RBACService.getPermission(
                RBACService.RBAC_F.F_SITES,
                RBACService.RBAC_A.A_VIEW
              ),
              RBACService.getPermission(
                RBACService.RBAC_F.F_SITES_QR_CODES,
                RBACService.RBAC_A.A_EDIT
              )
            ],
            true
          )
        ) {
          this.rbac.showGenericAccessDeniedMessage(
            'You do not have permission to edit QR codes.'
          );
          this.router.navigate([
            this.utils.prepareSiteRelatedLink(
              this.site_id,
              this.child_id ? this.parent_id : null,
              'edit/codes'
            )
          ]);
          return;
        }

        this.api
          .makeRequest(
            'get',
            'v1/sites/' + this.site_id + '/qrcodes/' + this.code.id
          )
          .then((response: any) => {
            this.code.apply(response.data);
          });

        // this.service.get(this.site_id, this.code.id);
      } else {
        // Creating new QR code - check create permission only (standalone)
        if (
          !this.rbac.userCanAccess(
            [
              RBACService.getPermission(
                RBACService.RBAC_F.F_SITES,
                RBACService.RBAC_A.A_VIEW
              ),
              RBACService.getPermission(
                RBACService.RBAC_F.F_SITES_QR_CODES,
                RBACService.RBAC_A.A_CREATE
              )
            ],
            true
          )
        ) {
          this.rbac.showGenericAccessDeniedMessage(
            'You do not have permission to create QR codes.'
          );
          this.router.navigate([
            this.utils.prepareSiteRelatedLink(
              this.site_id,
              this.child_id ? this.parent_id : null,
              'edit/codes'
            )
          ]);
          return;
        }
      }
    }
  }

  onRemove() {
    // Check permissions - user must have sites:view AND sites-qr-codes:view AND sites-qr-codes:delete
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_QR_CODES,
            RBACService.RBAC_A.A_DELETE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete QR codes.'
      );
      return;
    }

    this.utils.showModal(
      'Remove QR Code',
      'Are you sure you want to remove this QR code?',
      () => {
        this.api
          .makeRequest(
            'delete',
            'v1/sites/' + this.site_id + '/qrcodes/' + this.code.id
          )
          .then((response: any) => {
            this.utils.showToast('The QR code was removed.');
            this.router.navigate([
              this.utils.prepareSiteRelatedLink(
                this.site_id,
                this.child_id ? this.parent_id : null,
                'edit/codes'
              )
            ]);
          });
      }
    );
  }

  onSubmit(form: NgForm) {
    // make sure the form data is valid
    if (form.valid) {
      if (this.inProgress) {
        return;
      }

      this.inProgress = true;

      // check if the site should be created or updated
      if (this.code.id) {
        // Updating - check view + edit permissions
        if (
          !this.rbac.userCanAccess(
            [
              RBACService.getPermission(
                RBACService.RBAC_F.F_SITES,
                RBACService.RBAC_A.A_VIEW
              ),
              RBACService.getPermission(
                RBACService.RBAC_F.F_SITES_QR_CODES,
                RBACService.RBAC_A.A_EDIT
              )
            ],
            true
          )
        ) {
          this.rbac.showGenericAccessDeniedMessage(
            'You do not have permission to edit QR codes.'
          );
          this.inProgress = false;
          return;
        }

        this.api
          .makeRequest(
            'put',
            'v1/sites/' + this.site_id + '/qrcodes/' + this.code.id,
            this.code
          )
          .then((response: any) => {
            this.utils.showToast('The QR code was updated');
            this.inProgress = false;
          })
          .catch((err: any) => {
            this.inProgress = false;
          });

        // update the site
        // this.service.update(this.site_id);
      } else {
        // Creating - check create permission only (standalone)
        if (
          !this.rbac.userCanAccess(
            [
              RBACService.getPermission(
                RBACService.RBAC_F.F_SITES,
                RBACService.RBAC_A.A_VIEW
              ),
              RBACService.getPermission(
                RBACService.RBAC_F.F_SITES_QR_CODES,
                RBACService.RBAC_A.A_CREATE
              )
            ],
            true
          )
        ) {
          this.rbac.showGenericAccessDeniedMessage(
            'You do not have permission to create QR codes.'
          );
          this.inProgress = false;
          return;
        }

        this.api
          .makeRequest(
            'post',
            'v1/sites/' + this.site_id + '/qrcodes',
            this.code
          )
          .then((response: any) => {
            this.utils.showToast('The QR code was added');
            this.inProgress = false;
            this.router.navigate([
              this.utils.prepareSiteRelatedLink(
                this.site_id,
                this.child_id ? this.parent_id : null,
                'edit/codes'
              )
            ]);
          })
          .catch((err: any) => {
            this.inProgress = false;
          });

        // create the site
        // this.service.add(this.site_id, (response) => {
        //  // and navigate back to the list of sites
        //  this.router.navigate(['/sites', this.site_id, 'codes']);
        // });
      }
    } else {
      alert('Please make sure you enter all fields with valid information.');
    }
  }

  protected readonly RBACService = RBACService;
}
